import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, ArrowUpRight } from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";

import { BUSINESS } from "@/lib/business-info";
import {
    STORY_PARAGRAPHS,
    PULL_QUOTE,
    PILLARS,
    REVIEWS,
    COMMUNITY_LINES,
} from "@/lib/about-data";

const HERO_BG =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/2s6v3098_56BE41F0-CED7-4829-B4F0-293F7449CF20.PNG";

const STORY_PORTRAIT =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/uowmbhf2_24B584D4-D59C-4E4E-97AC-8CB525C35052.PNG";

const STORY_SECONDARY =
    "https://customer-assets.emergentagent.com/job_b8a7810c-24f2-4266-91d4-0b33b2eb0b66/artifacts/bwzczki2_6AED80A2-E5AD-4A8F-A910-849ACAE56061%202.PNG";

const COMMUNITY_BG =
    "https://images.unsplash.com/photo-1606660023296-81d67734170a?auto=format&fit=crop&w=2200&q=85";

const HERITAGE_BG =
    "https://static.prod-images.emergentagent.com/jobs/c2b7593f-ae8f-4892-a9ba-b424e7885b58/images/bb2057e7db338c750b2b93cb1afe76d4f202726fe38b41fa33fe63ac126a855a.png";

const COMMUNITY_PHOTO =
    "https://customer-assets.emergentagent.com/job_b8a7810c-24f2-4266-91d4-0b33b2eb0b66/artifacts/gz4ydjl5_CE2C979B-83C4-417C-A61B-198E9E76761B.PNG";

// ---------------- Hero ----------------
function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
    const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.45]);

    return (
        <section
            ref={ref}
            data-testid="about-hero"
            className="relative min-h-[92svh] md:min-h-[100svh] overflow-hidden bg-charcoal"
            aria-label="About hero"
        >
            <motion.div
                style={{ y }}
                aria-hidden
                className="absolute inset-0 z-0 will-change-transform"
            >
                <div
                    className="absolute inset-0 bg-charcoal bg-no-repeat"
                    style={{
                        backgroundImage: `url(${HERO_BG})`,
                        backgroundPosition: "right 40%",
                        backgroundSize: "50% auto",
                        filter: "brightness(1.02) blur(1.4px)",
                    }}
                />
                {/* Subtle cinematic charcoal wash: dark left for headline, kitchen detail preserved right */}
                <div className="absolute inset-0 bg-charcoal/20" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(to right, rgba(18,18,18,0.90) 0%, rgba(18,18,18,0.72) 32%, rgba(18,18,18,0.34) 62%, rgba(18,18,18,0.10) 90%, rgba(18,18,18,0) 100%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/35 via-transparent to-charcoal/75" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_22%_55%,rgba(0,0,0,0.32),rgba(0,0,0,0)_70%)]" />
                {/* Tame the brightest ceiling lights & white prep-area highlights in the upper-right only */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_38%_40%_at_100%_0%,rgba(18,18,18,0.55),rgba(0,0,0,0)_56%)]" />
            </motion.div>

            <motion.div
                style={{ opacity }}
                className="relative z-10 empire-container min-h-[92svh] md:min-h-[100svh] flex flex-col justify-center pt-36 pb-24"
            >
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="empire-eyebrow"
                >
                    <span className="inline-block h-px w-8 bg-gold align-middle mr-3" />
                    Our Story · Est. on Main Street, Poughkeepsie
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="about-headline"
                    className="mt-8 max-w-5xl font-serif text-cream text-[2.6rem] leading-[1.04] tracking-[-0.01em] md:text-[4.4rem] lg:text-[5.4rem]"
                >
                    A Kingston Kitchen.<br className="hidden md:block" />{" "}
                    <em className="italic font-normal text-gold">A Hudson Valley Home.</em>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.32 }}
                    className="mt-8 max-w-2xl text-cream/82 text-[15px] md:text-[17px] font-light leading-[1.75]"
                >
                    Empire Jamaica Fusion brings authentic Jamaican flavors and warm
                    Caribbean hospitality to Poughkeepsie.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="mt-12 flex flex-col sm:flex-row gap-4"
                >
                    <a
                        href="/#catering-inquiry"
                        data-testid="about-cta-catering"
                        className="empire-btn-primary"
                    >
                        Book Catering
                        <ArrowRight size={14} />
                    </a>
                    <Link
                        to="/menu"
                        data-testid="about-cta-menu"
                        className="empire-btn-secondary"
                    >
                        View Menu
                    </Link>
                </motion.div>
            </motion.div>

            <div
                aria-hidden
                className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-cream/40 text-[10px] uppercase tracking-[0.32em]"
            >
                <span>Read</span>
                <span className="block h-10 w-px bg-gradient-to-b from-gold/70 to-transparent" />
            </div>
        </section>
    );
}

// ---------------- Story (editorial feature) ----------------
function Story() {
    return (
        <section
            id="story"
            data-testid="about-story"
            className="relative empire-section bg-charcoal"
            aria-label="Our story"
        >
            <div className="empire-container">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    {/* Editorial copy */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-7"
                    >
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                A Feature From The Kitchen
                            </span>
                        </div>
                        <h2 className="mt-6 font-serif text-cream text-[2.2rem] md:text-[3.2rem] lg:text-[3.6rem] leading-[1.05] tracking-[-0.01em]">
                            From Kingston<br />
                            <span className="italic text-gold">to Main Street.</span>
                        </h2>

                        <div className="mt-10 space-y-7 max-w-[60ch] text-cream/82 font-light text-[15.5px] md:text-[17px] leading-[1.85]">
                            {STORY_PARAGRAPHS.map((p, i) => (
                                <p key={i} data-testid={`story-paragraph-${i}`}>
                                    {/* Drop cap on first paragraph */}
                                    {i === 0 ? (
                                        <>
                                            <span className="font-serif text-gold text-[3.6rem] md:text-[4.4rem] leading-[0.85] float-left mr-3 mt-1">
                                                {p[0]}
                                            </span>
                                            {p.slice(1)}
                                        </>
                                    ) : (
                                        p
                                    )}
                                </p>
                            ))}
                        </div>
                    </motion.div>

                    {/* Imagery column */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                            duration: 0.9,
                            delay: 0.15,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="lg:col-span-5 relative lg:sticky lg:top-36"
                    >
                        <div className="relative aspect-[4/5] overflow-hidden">
                            <img
                                src={STORY_PORTRAIT}
                                alt="Empire Jamaica Fusion storefront on Main Street, Poughkeepsie"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-charcoal/15" />
                            <div className="absolute -inset-px border border-gold/15 pointer-events-none" />
                        </div>
                        <div className="hidden md:block absolute -bottom-12 -left-10 w-2/3 aspect-[4/5] overflow-hidden border-[6px] border-charcoal">
                            <img
                                src={STORY_SECONDARY}
                                alt="Authentic Empire Jamaica Fusion escovitch fish"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-burgundy/12 mix-blend-multiply" />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

// ---------------- Pull quote ----------------
function PullQuote() {
    return (
        <section
            data-testid="about-pull-quote"
            className="relative bg-charcoal-800 border-y border-cream/[0.08]"
            aria-label="Pull quote"
        >
            <div className="empire-container py-24 md:py-36 text-center max-w-4xl mx-auto">
                <span aria-hidden className="font-serif text-gold text-[5rem] leading-none italic">
                    “
                </span>
                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="-mt-4 font-serif italic text-cream/95 text-[1.7rem] md:text-[2.4rem] lg:text-[2.7rem] leading-[1.25] tracking-tight"
                >
                    {PULL_QUOTE.text}
                </motion.p>
                <div className="mt-10 inline-flex items-center gap-4">
                    <span className="h-px w-12 bg-gold/60" />
                    <span className="empire-eyebrow !text-cream/55">
                        {PULL_QUOTE.attribution}
                    </span>
                    <span className="h-px w-12 bg-gold/60" />
                </div>
            </div>
        </section>
    );
}

// ---------------- Hospitality Pillars ----------------
function Pillars() {
    return (
        <section
            id="pillars"
            data-testid="about-pillars"
            className="relative empire-section bg-charcoal"
            aria-label="How we host"
        >
            <div className="empire-container">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-end mb-14 md:mb-20">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                How We Host
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.2rem] md:text-[3.2rem] lg:text-[3.6rem] leading-[1.05] tracking-[-0.01em]">
                            Three things we never<br />
                            <span className="italic text-gold">cut corners on.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        After five years of cooking for the Hudson Valley, the
                        room runs on instinct. These are the three instincts we
                        protect most carefully.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-cream/[0.07]">
                    {PILLARS.map((p, i) => (
                        <motion.article
                            key={p.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.85,
                                delay: i * 0.1,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`pillar-${p.id}`}
                            className="group relative bg-charcoal-800 flex flex-col"
                        >
                            <div className="relative aspect-[5/4] overflow-hidden">
                                <img
                                    src={p.image}
                                    alt=""
                                    aria-hidden
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.05]"
                                />
                                <div className="absolute inset-0 bg-charcoal/25" />
                                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-800 to-transparent" />
                                <span
                                    aria-hidden
                                    className="absolute top-5 left-5 font-serif italic text-gold text-[14px] tracking-[0.15em]"
                                >
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <div className="p-7 md:p-9">
                                <h3 className="font-serif text-cream text-[1.5rem] md:text-[1.7rem] leading-[1.15] tracking-tight">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-cream/72 text-[14px] md:text-[14.5px] font-light leading-[1.75]">
                                    {p.body}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------------- Community ----------------
function Community() {
    // Slight infinite marquee of customer moments
    const doubled = [...COMMUNITY_LINES, ...COMMUNITY_LINES];
    return (
        <section
            id="community"
            data-testid="about-community"
            className="relative overflow-hidden bg-charcoal"
            aria-label="The neighborhood"
        >
            <div aria-hidden className="absolute inset-0 z-0">
                <img
                    src={COMMUNITY_BG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-charcoal/75" />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/40 to-charcoal" />
            </div>

            <div className="relative z-10 empire-container py-24 md:py-36">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-end mb-12 md:mb-16">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                The Hudson Valley Table
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.2rem] md:text-[3.2rem] lg:text-[3.6rem] leading-[1.05] tracking-[-0.01em]">
                            Strangers became regulars.<br />
                            <span className="italic text-gold">Regulars became family.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        Couples meet here. Children grow up here. Office teams come
                        back every December. Empire isn’t a restaurant the
                        neighborhood discovered. It’s a restaurant the neighborhood
                        claimed.
                    </p>
                </div>

                {/* Community recognition photo */}
                <motion.figure
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="about-community-photo"
                    className="max-w-3xl mx-auto"
                >
                    <div className="relative overflow-hidden border border-cream/[0.08]">
                        <img
                            src={COMMUNITY_PHOTO}
                            alt="The Empire Jamaica Fusion team in their Poughkeepsie kitchen"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-auto object-cover"
                        />
                        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/15" />
                    </div>
                    <figcaption className="mt-5 text-center text-cream/65 text-sm font-light italic">
                        The faces behind Empire Jamaica Fusion in Poughkeepsie.
                    </figcaption>
                </motion.figure>

                {/* Quiet horizontal scroll of customer-described moments */}
                <div
                    className="relative -mx-6 md:-mx-10 lg:-mx-14 mt-4 no-scrollbar overflow-hidden"
                    aria-hidden
                >
                    <div className="flex gap-10 md:gap-14 animate-marquee-x whitespace-nowrap py-8">
                        {doubled.map((line, i) => (
                            <span
                                key={i}
                                className="font-serif italic text-cream/80 text-[1.8rem] md:text-[2.4rem] lg:text-[3rem] leading-none tracking-tight"
                            >
                                {line}
                                <span className="text-gold/80 ml-10">·</span>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------------- Reviews ----------------
function Stars({ value = 5 }) {
    return (
        <div className="flex items-center gap-0.5" aria-label={`${value} star rating`}>
            {Array.from({ length: 5 }).map((_, i) => (
                <Star
                    key={i}
                    size={13}
                    strokeWidth={1.2}
                    className={
                        i < Math.round(value)
                            ? "fill-gold text-gold"
                            : "text-cream/20"
                    }
                />
            ))}
        </div>
    );
}

function Reviews() {
    return (
        <section
            id="reviews"
            data-testid="about-reviews"
            className="relative empire-section bg-charcoal-800"
            aria-label="What guests say"
        >
            <div className="empire-container">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-end mb-14 md:mb-20">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                In Their Own Words
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.2rem] md:text-[3.2rem] lg:text-[3.6rem] leading-[1.05] tracking-[-0.01em]">
                            What guests<br />
                            <span className="italic text-gold">tell us afterwards.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        From 186 verified Google reviews — and a thousand thank-yous
                        on the way out the door.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-cream/[0.07]">
                    {REVIEWS.map((r, i) => (
                        <motion.figure
                            key={i}
                            initial={{ opacity: 0, y: 22 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{
                                duration: 0.8,
                                delay: (i % 3) * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`review-card-${i}`}
                            className="bg-charcoal p-8 md:p-10 flex flex-col gap-6"
                        >
                            <Stars value={r.rating} />
                            <blockquote className="font-serif italic text-cream/95 text-[1.15rem] md:text-[1.3rem] leading-[1.45] tracking-tight">
                                “{r.text}”
                            </blockquote>
                            <figcaption className="mt-auto">
                                <div className="font-serif text-gold text-base tracking-tight">
                                    {r.name}
                                </div>
                                <div className="text-cream/45 text-[11px] uppercase tracking-[0.22em] mt-1">
                                    {r.meta}
                                </div>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>

                <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <a
                        href="https://www.google.com/maps/place/Empire+Jamaica+Fusion+Restuarant/@41.7024529,-73.9239266,17z/data=!4m8!3m7!1s0x89dd3f17ad39d653:0xaaa3996045d78a3!8m2!3d41.7024529!4d-73.9239266!9m1!1b1!16s%2Fg%2F11sw_1h808"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="about-reviews-google"
                        className="empire-btn-secondary"
                    >
                        Read All Google Reviews
                        <ArrowUpRight size={14} />
                    </a>
                    <p className="text-cream/50 text-[13px] font-light">
                        4.8 ★ average · 186 verified reviews · still counting.
                    </p>
                </div>
            </div>
        </section>
    );
}

// ---------------- Heritage ----------------
function Heritage() {
    return (
        <section
            id="heritage"
            data-testid="about-heritage"
            className="relative overflow-hidden bg-charcoal"
            aria-label="Heritage"
        >
            <div aria-hidden className="absolute inset-0 z-0">
                <img
                    src={HERITAGE_BG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/80 to-charcoal/40" />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 to-charcoal" />
            </div>

            <div className="relative z-10 empire-container py-24 md:py-36">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-8"
                    >
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Heritage
                            </span>
                        </div>
                        <h2 className="mt-6 font-serif text-cream text-[2.4rem] md:text-[3.6rem] lg:text-[4.4rem] leading-[1.05] tracking-[-0.01em]">
                            Out of many,<br />
                            <span className="italic text-gold">one people.</span>
                        </h2>
                        <p className="mt-8 max-w-2xl text-cream/82 text-[15.5px] md:text-[17px] font-light leading-[1.85]">
                            Jamaica’s motto, but it’s also how we set the table.
                            Around our food you’ll find Hudson Valley locals,
                            transplants, students from Marist and Vassar, off-shift
                            nurses, two-year regulars, and the staff family that
                            keeps everything moving on a Saturday at 8 PM.
                        </p>
                        <p className="mt-5 max-w-2xl text-cream/65 text-[15px] font-light leading-[1.8] italic">
                            Different rooms. Different reasons. Same table.
                        </p>
                    </motion.div>

                    <motion.aside
                        initial={{ opacity: 0, y: 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{
                            duration: 0.9,
                            delay: 0.15,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="lg:col-span-4 border-l border-gold/30 pl-8 md:pl-10"
                    >
                        <span className="empire-eyebrow !text-cream/55">
                            What we carry from Kingston
                        </span>
                        <ul className="mt-6 space-y-4 text-cream/85 font-serif text-[1.2rem] md:text-[1.35rem] tracking-tight">
                            <li className="italic text-gold/90">Patience.</li>
                            <li>The smell of pimento on the grill.</li>
                            <li>The way you greet a guest at the door.</li>
                            <li>Sunday cooking as an event.</li>
                            <li>Hospitality you can feel before you taste anything.</li>
                        </ul>
                    </motion.aside>
                </div>
            </div>
        </section>
    );
}

// ---------------- Final CTA ----------------
function FinalCTA() {
    return (
        <section
            data-testid="about-final-cta"
            className="relative overflow-hidden bg-charcoal"
            aria-label="Visit"
        >
            <div aria-hidden className="absolute inset-0 z-0">
                <img
                    src={HERO_BG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-30"
                />
                <div className="absolute inset-0 bg-burgundy/30" />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/85 via-burgundy/45 to-charcoal" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.10),rgba(0,0,0,0)_55%)]" />
            </div>

            <div className="relative z-10 empire-container py-28 md:py-40 text-center">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="empire-eyebrow !text-gold"
                >
                    There’s A Seat For You
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    data-testid="about-final-heading"
                    className="mt-6 mx-auto max-w-4xl font-serif text-cream text-[2.4rem] md:text-[4rem] lg:text-[4.6rem] leading-[1.05] tracking-[-0.01em]"
                >
                    Come once and the menu sells you.<br />
                    <span className="italic text-gold">Come twice and Michelle knows your name.</span>
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.3 }}
                    className="mt-8 max-w-xl mx-auto text-cream/80 text-[15.5px] md:text-[17px] font-light leading-[1.75]"
                >
                    After that, we&rsquo;ll save the table by the window.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.45 }}
                    className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href={BUSINESS.phoneLink}
                        data-testid="about-final-call"
                        className="inline-flex items-center justify-center gap-2 bg-cream text-charcoal px-8 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-gold transition-colors duration-300"
                    >
                        Reserve A Table
                        <ArrowRight size={14} />
                    </a>
                    <Link
                        to="/private-events"
                        data-testid="about-final-private"
                        className="inline-flex items-center justify-center gap-2 border border-cream/60 text-cream px-8 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-cream hover:text-charcoal transition-colors duration-300"
                    >
                        Plan A Gathering
                    </Link>
                </motion.div>

                <p className="mt-12 text-cream/45 text-[12px] uppercase tracking-[0.28em]">
                    385 Main Street · Poughkeepsie, NY · (845) 849-0497
                </p>
            </div>
        </section>
    );
}

// ---------------- Page ----------------
export default function About() {
    useEffect(() => {
        document.title = "About · Empire Jamaica Fusion · Poughkeepsie, NY";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <>
            <Nav />
            <main data-testid="about-page" className="overflow-x-hidden">
                <Hero />
                <Story />
                <PullQuote />
                <Pillars />
                <Community />
                <Reviews />
                <Heritage />
                <FinalCTA />
            </main>
            <Footer />
            <MobileStickyCTA />
        </>
    );
}
